import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AuthModal from './AuthModal';
import VideoPlayerModal from './VideoPlayerModal';
import { Play, Heart, Eye, BadgeCheck, Film } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VideoFeed() {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    const fetchWithRetry = async (retries = 2) => {
      for (let i = 0; i <= retries; i++) {
        try {
          const r = await axios.get(`${API}/videos?limit=12`);
          setVideos(r.data);
          return;
        } catch (err) {
          if (i < retries) await new Promise(r => setTimeout(r, 1500 * (i + 1)));
        }
      }
    };
    fetchWithRetry();
  }, []);

  const handleVideoClick = async (video) => {
    try {
      const res = await axios.get(`${API}/videos/${video.video_id}`);
      setSelectedVideo(res.data);
    } catch {
      setSelectedVideo(video);
    }
  };

  if (videos.length === 0) return null;

  return (
    <>
      <section id="from-creators" className="mb-8 scroll-mt-24" data-testid="video-feed-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#FFF5F5] flex items-center justify-center">
              <Film size={14} className="text-[#D48585]" />
            </div>
            <h2 className="font-heading text-xl sm:text-2xl text-[#2C2A29]">From Creators</h2>
          </div>
        </div>

        <div className="flex overflow-x-auto gap-3 pb-3 hide-scrollbar" data-testid="video-feed-scroll">
          {videos.map((video) => {
            const author = video.author || {};
            return (
              <div
                key={video.video_id}
                onClick={() => handleVideoClick(video)}
                className="flex-shrink-0 w-[140px] sm:w-[180px] cursor-pointer group"
                data-testid={`video-card-${video.video_id}`}
              >
                {/* Thumbnail */}
                <div className="relative aspect-[9/12] rounded-xl overflow-hidden bg-[#2C2A29] mb-2">
                  {video.thumbnail_url ? (
                    <img src={video.thumbnail_url} alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#2C2A29] to-[#1a1918]">
                      <Film size={28} className="text-[#78716C]" />
                    </div>
                  )}
                  {/* Play overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-colors">
                    <div className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                      <Play size={14} className="text-[#2C2A29] ml-0.5" fill="#2C2A29" />
                    </div>
                  </div>
                  {/* Category badge */}
                  <div className="absolute top-2 left-2">
                    <span className="bg-black/50 backdrop-blur-sm text-white px-2 py-0.5 rounded-full text-[8px] font-body font-semibold">
                      {video.category}
                    </span>
                  </div>
                  {/* Stats overlay */}
                  <div className="absolute bottom-2 left-2 right-2 flex items-center gap-2">
                    <span className="flex items-center gap-0.5 text-white text-[10px] font-body">
                      <Heart size={10} />{video.likes_count || 0}
                    </span>
                    <span className="flex items-center gap-0.5 text-white text-[10px] font-body">
                      <Eye size={10} />{video.views_count || 0}
                    </span>
                  </div>
                </div>

                {/* Info */}
                <p className="font-body text-xs font-medium text-[#2C2A29] line-clamp-2 leading-snug mb-1" data-testid={`video-title-${video.video_id}`}>
                  {video.title}
                </p>
                <div className="flex items-center gap-1.5">
                  {author.picture ? (
                    <img src={author.picture} alt="" className="w-4 h-4 rounded-full object-cover" />
                  ) : (
                    <div className="w-4 h-4 rounded-full bg-blush-200 flex items-center justify-center text-[7px] font-bold text-[#2C2A29]">
                      {author.name?.[0]}
                    </div>
                  )}
                  <span className="text-xs font-body text-[#78716C] flex items-center gap-1">
                    {author.name}
                    <BadgeCheck size={12} className="text-blue-500 fill-blue-50" />
                  </span>
                  {author.is_demo && (
                    <span className="text-[10px] font-body font-semibold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full" data-testid={`demo-video-badge-${video.video_id}`}>
                      Demo
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <VideoPlayerModal
        video={selectedVideo}
        open={!!selectedVideo}
        onOpenChange={(open) => { if (!open) setSelectedVideo(null); }}
        onAuthRequired={() => setShowAuth(true)}
        onVideoDeleted={(id) => { setVideos(prev => prev.filter(v => v.video_id !== id)); setSelectedVideo(null); }}
        onVideoUpdated={(id, updates) => { setVideos(prev => prev.map(v => v.video_id === id ? { ...v, ...updates } : v)); setSelectedVideo(prev => prev ? { ...prev, ...updates } : prev); }}
      />
      <AuthModal open={showAuth} onOpenChange={setShowAuth} />
    </>
  );
}
